
import React, { useState, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../../spinner/Spinner";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import ApiServices from "../../../api/ApiServices";
import DataTable from "react-data-table-component";
import CustomStyles from "../../../components/utils/CustomStyles";
import { toast } from "react-toastify";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import { useSwitchers, useBillers } from "../../../hooks/useAppData";
import { useQueryClient } from "@tanstack/react-query";

const Switcher = () => {
    const [selectedBiller, setSelectedBiller] = useState({});
    const [addToSwitcherLoading, setAddToSwitcherLoading] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [switchLoading, setSwitchLoading] = useState({});
    const [switchError, setSwitchError] = useState(null);
    const [switchSuccess, setSwitchSuccess] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [selectedProductName, setSelectedProductName] = useState("");
    const [selectedProductCategoryId, setSelectedProductCategoryId] = useState("");
    const [selectedBillerId, setSelectedBillerId] = useState("");

    const { permissions } = useContext(AuthContext);
    const navigate = useNavigate();
    const axiosInst = AxiosApiCall();
    const queryClient = useQueryClient();

    const { data: switchersResponse, isLoading: loading } = useSwitchers();
    const products = switchersResponse?.data?.switchers || [];
    const switches = switchersResponse?.data?.switches || [];
    const productsListSelection = switchersResponse?.data?.products || [];

    const { data: billersResponse } = useBillers();
    const billerName = billersResponse?.data || [];

    // Function to format permission text
    const formatText = (text) => {
        let formattedText = text.replace(/_/g, " ");
        formattedText = formattedText
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
        return formattedText;
    };


    const handleProductChange = (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex]; // Get selected option element
        const selectedProductValue = e.target.value; // Get product id
        const selectedProductNameValue = selectedOption.text; // Get product name
        const selectedCategoryId = selectedOption.dataset.categoryId; // Get category_id from data attribute
        // const selectedBillerIdValue = selectedOption.dataset.billerId; // Get biller_id from data attribute

        setSelectedProduct(selectedProductValue);
        setSelectedProductName(selectedProductNameValue);
        setSelectedProductCategoryId(selectedCategoryId);
        // setSelectedBillerId(selectedBillerIdValue);

        // console.log("Selected product ID: ", selectedProductValue);
        // console.log("Selected product name:", selectedProductNameValue);
        // console.log("Selected Category ID:", selectedCategoryId);
        // console.log("Selected Biller ID:", selectedBillerIdValue);

    };



    // Handle biller change in dropdown
    const handleBillerChange = (productId, newBillerId) => {
        setSelectedBiller((prev) => ({
            ...prev,
            [productId]: newBillerId,
        }));

    };

    const handleFormBillerChange = (e) => {
        setSelectedBillerId(e.target.value);
    };


    const getSwitchesForCategory = (category_id) => {
        const filteredSwitches = switches.filter(
            (switchItem) => switchItem.category_id === category_id
        );

        // console.log("Category ID:", category_id);
        // console.log("Filtered Switches:", filteredSwitches);
        return filteredSwitches;
    };



    // Handle switching the biller
    const handleSwitch = async (productId) => {
        const selectedBillerId = selectedBiller[productId];
        if (!selectedBillerId) {
            setSwitchError("Please select a biller.");
            return;
        }

        setSwitchLoading((prev) => ({ ...prev, [productId]: true }));
        setSwitchError(null);
        setSwitchSuccess(null);

        const formData = {
            id: productId,
            biller_id: selectedBillerId,
        };
        // console.log("This is the switch credentials", formData);
        try {
            const response = await axiosInst.post(ApiServices.switch, formData, {
                withCredentials: true,
            });
            const { status, message } = response.data;
            if (status === 200) {
                toast.success(message, { toastId: "switchs-successfully-switched" });
                queryClient.invalidateQueries({ queryKey: ["switchers"] });
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            setSwitchError(error.message || "An error occurred while switching.");
        } finally {
            setSwitchLoading((prev) => ({ ...prev, [productId]: false }));
        }
    };

    //Validation function
    const validateForm = () => {
        if (!selectedProduct) {
            toast.error("Please select a product.");
            return false;
        }
        if (!selectedBillerId) {
            toast.error("Please select a biller.");
            return false;
        }
        return true;
    };
    // Handle submit to create new switcher
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setAddToSwitcherLoading(true);

        // console.log("Before form data creation:");
        // console.log("Selected Product ID: ", selectedProduct);
        // console.log("Selected Product Name: ", selectedProductName);
        // console.log("Selected Product Category ID: ", selectedProductCategoryId);
        // console.log("Selected Biller ID: ", selectedBillerId);

        const formData = {
            product_id: selectedProduct,
            product_name: selectedProductName,
            product_category: selectedProductCategoryId,
            biller_id: selectedBillerId
        };

        // console.log("this is my form data", formData);
        try {
            const response = await axiosInst.post(ApiServices.addSwitchers, formData, {
                withCredentials: true,
            });

            const { status, message } = response.data;
            if (status === 200 || status === "200") {
                toast.success(message, { toastId: "switchs-created-success" });
                setSelectedProduct("");
                setSelectedProductName("");
                setSelectedProductCategoryId("");
                setSelectedBillerId("");
                queryClient.invalidateQueries({ queryKey: ["switchers"] });
            } else {
                handleError({ response: { status, data: { message } } }, navigate);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setAddToSwitcherLoading(false);
        }
    };

    // Filter products by name
    const handleFilter = (e) => {
        setFilterText(e.target.value);
    };

    // Columns definition
    const columns = [
        {
            name: "Product Name",
            selector: (row) => row.name ? formatText(row.name).toUpperCase() : "NOT AVAILABLE",
            sortable: false,
            width: "300px",
        },
        {
            name: "Current Biller",
            selector: (row) => row.biller ? formatText(row.biller).toUpperCase() : "NOT AVAILABLE",
            sortable: true,
            width: "300px",
        },
        {
            name: "Switch To Available Biller",
            cell: (row) => {
                //  Retrieve available switches based on category
                const availableSwitches = getSwitchesForCategory(row.category_id);
                // console.log("ava swich", availableSwitches);
                return (
                    <div className="flex flex-col">
                        <select
                            className="mx-2 my-1 px-4 py-2 text-black border rounded-md bg-dGrey3"
                            value={selectedBiller[row.id] || row.biller_id}
                            onChange={(e) => handleBillerChange(row.id, e.target.value)}
                        >
                            {availableSwitches.length > 0 ? (
                                availableSwitches.map((switchItem) => (
                                    <option key={switchItem.billerId} value={switchItem.billerId}>
                                        {/* <option key={`${switchItem.billerId}-${switchItem.id}`} value={switchItem.billerId}></option> */}
                                        {switchItem.biller}
                                    </option>
                                ))
                            ) : (
                                <option value="">No available billers</option>
                            )}
                        </select>
                        {permissions.some(permission => permission.name === 'switcher.create') && (
                            <button
                                type="button"
                                className="text-dGrey3 bg-darkGreen3 border-0 py-2 px-4 my-1 focus:outline-none hover:bg-blue-600 rounded font-medium"
                                onClick={() => handleSwitch(row.id)}
                                disabled={switchLoading[row.id]}
                            >
                                {switchLoading[row.id] ? "Switching..." : "Switch"}
                            </button>
                        )}
                    </div>
                );
            },
            width: "400px",
        }
    ];

    // Filtered and sorted data
    const filteredItems = (products && Array.isArray(products)) ?
        products.filter((item) =>
            Object.values(item).some((value) =>
                typeof value === "string" && value.toLowerCase().includes(filterText.toLowerCase())
            )
        ) : [];

    const data = (filteredItems && Array.isArray(filteredItems)) ?
        filteredItems.map((product) => ({
            name: product.name || "NOT AVAILABLE",
            biller: product.biller || "NOT AVAILABLE",
            biller_id: product.biller_id,
            id: product.id,
            category_id: product.category_id,
        })) : [];

    const sortedData = data.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA; // recent first
    });




    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Switchers</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">All</p>
                            </li>
                        </ol>
                    </nav>
                </section>
                <section className="gray_background mb-2">
                    <div className="container px-1 pb-2 mx-auto">
                        <div className="lg:w-full md:w-full bg-white rounded-lg py-3 px-2 flex flex-col md:ml-auto w-full mt-10 md:mt-0 relative z-10 shadow-md bg-dGrey3">
                            <form
                                onSubmit={handleSubmit}
                                className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-end"
                            >
                                <select
                                    id="product_name"
                                    name="product_name"
                                    value={selectedProduct || ""}
                                    onChange={handleProductChange}
                                    className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                                >
                                    <option value="">Select Product</option>
                                    {/* Add safety check here */}
                                    {productsListSelection && productsListSelection.length > 0 ? (
                                        productsListSelection.map((prod) => (
                                            <option
                                                key={prod.id}
                                                value={prod.id}
                                                data-category-id={prod.category_id}
                                                data-biller-id={prod.biller_id}
                                            >
                                                {prod.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">Loading products...</option>
                                    )}
                                </select>

                                <select
                                    id="biller_id"
                                    name="biller_id"
                                    value={selectedBillerId}
                                    onChange={handleFormBillerChange}
                                    className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                                >
                                    <option value="">Select Biller</option>
                                    {/* Add safety check here */}
                                    {billerName && billerName.length > 0 ? (
                                        billerName.map((billr) => (
                                            <option key={billr.id} value={billr.id}>
                                                {billr.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">Loading billers...</option>
                                    )}
                                </select>
                                <div className="w-full mb-0 ">
                                    {/* switcher.create */}
                                    {permissions.some(permission => permission.name === 'switcher.create') && (
                                        <button
                                            type="submit"
                                            className="w-full bg-darkGreen3 text-dGrey1 rounded-lg py-2 mt-4"
                                            disabled={addToSwitcherLoading}
                                        >
                                            {addToSwitcherLoading ? "Adding To Switch..." : "Add To Switch"}
                                        </button>

                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </section>
                <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
                    <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
                        <input
                            type="search"
                            className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                            placeholder="Search"
                            value={filterText}
                            onChange={handleFilter}
                        />
                    </div>

                </div>


                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <DataTable
                        columns={columns}
                        data={sortedData}
                        pagination
                        striped
                        responsive
                        highlightOnHover
                        noDataComponent={<div>No products found</div>}
                        customStyles={CustomStyles}
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default Switcher;

